import { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Globe, Link, MessageCircle, ShieldCheck, X } from 'lucide-react';
import { datadogRum } from '@datadog/browser-rum';
import { useCrispChat } from '../Crisp/CrispProvider';
import { useB2bStoreInvite } from '../../hooks/useB2bStoreInvite';
import PromosAPI from '../../api/PromosAPI';
import { pickB2bInviteCreative } from '../../utils/b2bInvite';
import invite1 from '../../assets/images/banners/b2b-invite/b2b-invite-1.svg';
import invite2 from '../../assets/images/banners/b2b-invite/b2b-invite-2.svg';
import invite3 from '../../assets/images/banners/b2b-invite/b2b-invite-3.svg';
import invite4 from '../../assets/images/banners/b2b-invite/b2b-invite-4.svg';

const CREATIVES = [invite1, invite2, invite3, invite4] as const;

type B2bStoreInviteModalProps = {
  isOpen: boolean;
  onDismiss: (source: 'close' | 'overlay') => void;
  onCheckDetails: () => void;
  onOpenChat: () => void;
};

export function B2bStoreInviteModal({
  isOpen,
  onDismiss,
  onCheckDetails,
  onOpenChat,
}: B2bStoreInviteModalProps) {
  const { t } = useTranslation();
  const heroSrc = pickB2bInviteCreative(CREATIVES);

  useEffect(() => {
    if (!isOpen) return;
    datadogRum.addAction('B2B Modal appears');
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    return null;
  }

  const pillars = [
    { icon: CalendarDays, titleKey: 'b2bInvite.modal.store.title', textKey: 'b2bInvite.modal.store.text' },
    { icon: Link, titleKey: 'b2bInvite.modal.bookings.title', textKey: 'b2bInvite.modal.bookings.text' },
    { icon: ShieldCheck, titleKey: 'b2bInvite.modal.privacy.title', textKey: 'b2bInvite.modal.privacy.text' },
    { icon: Globe, titleKey: 'b2bInvite.modal.reach.title', textKey: 'b2bInvite.modal.reach.text' },
  ] as const;

  const modal = (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-3 sm:p-6"
      onClick={() => onDismiss('overlay')}
      role="presentation"
    >
      <style>{`
        @keyframes b2bInviteCardIn {
          from { opacity: 0; transform: translateX(-12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .b2b-invite-card {
          opacity: 0;
          animation: b2bInviteCardIn 700ms ease-out forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .b2b-invite-card {
            animation: none;
            opacity: 1;
            transform: none;
          }
        }
      `}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="b2b-invite-title"
        className="relative w-full max-w-[880px] max-h-[90dvh] overflow-y-auto rounded-2xl bg-[#fffdf7] shadow-2xl dark:bg-card"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative h-48 overflow-hidden sm:h-64">
          <img
            src={heroSrc}
            alt={t('b2bInvite.modal.imageAlt') || 'Bridal boutique on eAteliers'}
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
          <button
            type="button"
            onClick={() => onDismiss('close')}
            aria-label={t('b2bInvite.modal.close') || 'Close'}
            className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white transition hover:bg-black/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="space-y-6 px-5 py-6 sm:px-8 sm:py-8">
          <div className="space-y-2 text-center sm:text-left">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-800 dark:text-amber-200">
              {t('b2bInvite.modal.kicker')}
            </p>
            <h2 id="b2b-invite-title" className="text-2xl font-semibold text-gray-900 sm:text-3xl">
              {t('b2bInvite.modal.title')}
            </h2>
            <p className="text-base leading-relaxed text-gray-700">
              {t('b2bInvite.modal.body')}
            </p>
          </div>

          <ul className="grid gap-3 sm:grid-cols-2">
            {pillars.map(({ icon: Icon, titleKey, textKey }, index) => (
              <li
                key={titleKey}
                className="b2b-invite-card rounded-xl border border-amber-100 bg-white/80 p-4 dark:border-border dark:bg-muted"
                style={{ animationDelay: `${index * 180}ms` }}
              >
                <div className="mb-3 inline-flex rounded-full bg-amber-50 p-2 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  <Icon className="h-5 w-5" aria-hidden />
                </div>
                <h3 className="mb-1 font-semibold text-gray-900">{t(titleKey)}</h3>
                <p className="text-sm leading-relaxed text-gray-600">{t(textKey)}</p>
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={onOpenChat}
              data-dd-action-name="B2B Modal open chat"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-6 text-sm font-semibold text-gray-900 transition hover:border-gray-900 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-border dark:hover:bg-accent dark:focus-visible:ring-offset-background"
            >
              <MessageCircle className="h-4 w-4" aria-hidden />
              {t('b2bInvite.modal.cta.chat')}
            </button>
            <button
              type="button"
              onClick={onCheckDetails}
              data-dd-action-name="B2B Modal check details"
              className="inline-flex h-12 items-center justify-center rounded-lg bg-gray-900 px-6 text-sm font-semibold text-white transition hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90 dark:focus-visible:ring-offset-background"
            >
              {t('b2bInvite.modal.cta.plans')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, modalRoot);
}

export function B2bStoreInvite() {
  const { isOpen, close, force } = useB2bStoreInvite();
  const { openChat } = useCrispChat();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen || force) {
      return;
    }
    void PromosAPI.recordB2bStoreInviteView().catch((error) => {
      console.error('Failed to record B2B store-invite view', { error });
    });
  }, [isOpen, force]);

  const handleDismiss = (source: 'close' | 'overlay') => {
    datadogRum.addAction('B2B Modal dismissed', { source });
    close(true);
  };

  const handleCheckDetails = () => {
    datadogRum.addAction('B2B Modal check details');
    if (!force) {
      void PromosAPI.recordB2bStoreInviteConversion().catch((error) => {
        console.error('Failed to record B2B store-invite conversion', { error });
      });
    }
    close(true);
    navigate('/subscription');
  };

  const handleOpenChat = () => {
    datadogRum.addAction('B2B Modal open chat');
    if (!force) {
      void PromosAPI.recordB2bStoreInviteEngagement().catch((error) => {
        console.error('Failed to record B2B store-invite engagement', { error });
      });
    }
    close(true);
    openChat();
  };

  return (
    <B2bStoreInviteModal
      isOpen={isOpen}
      onDismiss={handleDismiss}
      onCheckDetails={handleCheckDetails}
      onOpenChat={handleOpenChat}
    />
  );
}

export default B2bStoreInvite;
